import { FC } from "react";
import { Button, ButtonSize, ButtonVariant, Hide } from "shared/components";
import { ApplicationActions } from "shared/utils/application-actions";

/**
 * Shared decision control for the application headers. It renders the "Undo
 * decision" button when a decision has been made and the "Make decision" button
 * otherwise, deriving visibility/enable/tooltip state from `ApplicationActions`.
 * The undo behavior (settings, mutation, modal) is owned by `useDecisionButton`.
 */
export const DecisionButton: FC<{
  appActions: ApplicationActions;
  /**
   * Opens the make-decision flow. Each view triggers it differently (the group
   * header branches on status to its decision-flow/confirmation modals, the
   * report header opens the MakeDecisionModal directly), so it is passed in.
   */
  onMakeDecision: () => void;
  /** Opens the shared undo-decision confirmation modal (from useDecisionButton). */
  onUndoDecision: () => void;
  makeDecisionLabel?: string;
}> = ({
  appActions,
  onMakeDecision,
  onUndoDecision,
  makeDecisionLabel = "Make decision"
}) => {
  const { makeDecision, undoDecision } = appActions;

  return (
    <>
      <Hide if={undoDecision.hidden}>
        <Button
          variant={ButtonVariant.BASE}
          size={ButtonSize.SM}
          onClick={onUndoDecision}
          disabled={undoDecision.disabled}
          tooltip={undoDecision.tooltip}
          tooltipZIndex={21}
        >
          Undo decision
        </Button>
      </Hide>
      <Hide if={makeDecision.hidden}>
        <Button
          variant={ButtonVariant.PRIMARY}
          size={ButtonSize.SM}
          onClick={onMakeDecision}
          className="hidden md:flex"
          disabled={makeDecision.disabled}
          tooltip={makeDecision.tooltip}
          tooltipPlacement="bottom-end"
          tooltipZIndex={2}
        >
          {makeDecisionLabel}
        </Button>
      </Hide>
    </>
  );
};
